import IconButton from 'components/atoms/button/IconButton'
import Form from 'components/atoms/form/Form'
import Icon from 'components/atoms/icon/Icon'
import Input from 'components/atoms/input/Input'
import Label from 'components/atoms/input/input-label/InputLabel'
import TextArea from 'components/atoms/text-area/TextArea'
import PrimaryDatePicker from 'components/molecules/datepicker/PrimaryDatePicker'
import { customStyles } from 'lib/helpers/configs/customStyles'
import { IconType } from 'lib/helpers/constants/iconTypes'
import withModal from 'lib/helpers/hoc/withModal'
import { useStorePending } from 'lib/hooks/mutations/Customers/useStorePending'
import React, { FC } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import CreatableSelect from 'react-select/creatable'


interface CreateFollowUpTaskProps {
    closeModal: () => void
    customerId: string
}

const CreateFollowUpTask: FC<CreateFollowUpTaskProps> = ({ closeModal, customerId }) => {

    const { t } = useTranslation()

    const {
        handleSubmit,
        register,
        formState: { errors },
        control,
        reset,
        watch,
    } = useForm()

    const { mutateAsync } = useStorePending()

    const onSubmit = (formData: Record<string, any>): void => {

        const dataWithId = {
            ...formData,
            category: formData.category === "Other" ? formData.other_category : formData.category,
            costumer_id: customerId,
        }

        mutateAsync(dataWithId)
            .then(() => {
                closeModal()
            })

    }

    return (
        <Form onSubmit={handleSubmit(onSubmit)}>
            <div className='bg-themeOpacity2 px-4 py-5 rounded-[20px] mb-10 flex flex-col gap-5'>
                <div className="relative">
                    <Label text={t("Issue Type")} />
                    <Controller
                        name="category"
                        control={control}
                        rules={{ required: `${t("This field is required")}` }}
                        render={({ field }) => (
                            <CreatableSelect<any, false>
                                {...field}
                                placeholder={t("Choose issue type")}
                                options={[
                                    {
                                        value: "Incorrect Information",
                                        label: `${t("Incorrect Information")}`,
                                    },
                                    {
                                        value: "Incomplete Information",
                                        label: `${t("Incomplete Information")}`,
                                    },
                                    {
                                        value: "Duplicate Customer",
                                        label: `${t("Duplicate Customer")}`,
                                    },
                                    {
                                        value: "Incomplete Documents",
                                        label: `${t("Incomplete Documents")}`,
                                    },
                                    {
                                        value: "Incorrect Documents",
                                        label: `${t("Incorrect Documents")}`,
                                    },
                                    {
                                        value: "Other",
                                        label: `${t("Other")}`,
                                    },
                                ]}
                                value={
                                    field.value
                                        ? { value: field.value, label: t(field.value) }
                                        : null
                                }
                                className="cursor-pointer font-inter-regular react-select-container"
                                classNamePrefix="react-select"
                                onChange={(newValue: any, actionMeta: any) => {
                                    if (
                                        actionMeta.action === "select-option" &&
                                        newValue?.value
                                    ) {
                                        field.onChange(newValue.value);
                                    }
                                }}
                                styles={customStyles}
                                isValidNewOption={() => false}
                                menuPosition={"fixed"}
                                menuPortalTarget={document.body}
                            />

                        )}
                    />
                    {errors.category?.message && (
                        <div className="font-inter-regular text-xs text-red-600 w-max absolute bottom-[-16px] 2xl:bottom-[-20px]  left-[2px]">
                            {errors.category?.message as string}
                        </div>
                    )}
                </div>

                {watch("category") === "Other" && (
                    <Input
                        label={t("Other Issue Type")}
                        placeholder={t("Other Issue Type")}
                        {...register("other_category", { required: `${t("This field is required")}` })}
                        error={errors?.other_category}
                    />
                )}
                <Input
                    label={t("Title")}
                    placeholder={t("Title")}
                    {...register("title", { required: `${t("This field is required")}` })}
                    error={errors?.title}
                />

                <Controller
                    name="deadline"
                    control={control}
                    render={({ field: { onChange, value } }) => (
                        <PrimaryDatePicker
                            label={t('Deadline')}
                            startDate={value}
                            setStartDate={(date: string) => onChange(date)}
                            className="md:text-[14px]  !p-[8px]"
                            wrapperClassName="!p-[8px] !w-[30vh]"
                        />
                    )}
                />
                <TextArea
                    label={t("Issue Description")}
                    placeholder={t("Describe the issue here")}
                    {...register("description", { required: `${t("Description is required")}` })}
                    error={errors?.description}
                />
            </div>
            <div className="flex gap-4 justify-end">
                <IconButton type='button' className='sm:min-w-[120px]' onClick={() => { reset() }} secondary>
                    {t("Reset")}
                </IconButton>
                <IconButton icon={<Icon iconType={IconType.PLUS_ICON} />} className='sm:min-w-[150px]'>
                    {t("Create")}
                </IconButton>
            </div>
        </Form>
    )
}

export default withModal(CreateFollowUpTask)